import axios from 'axios';
import { gtmPush } from './gtm';
import { getCachedBrowserCountry } from './geo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('ac_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    // Round 40o.7 — attach browser-resolved country as the PRIMARY
    // signal for the backend region-detection ladder. Synchronous
    // localStorage read, never blocks the request. If the cache is
    // empty (first hit, geo lookup still in flight, blocked by CSP,
    // localStorage disabled), the backend falls back to its existing
    // cf-ipcountry → vercel → ipapi.co ladder.
    const country = getCachedBrowserCountry();
    if (country) config.headers['X-Detected-Country'] = country;
    // Round 48k — attach the browser's IANA timezone as a synchronous
    // fallback for country inference. Fixes the signup race where the
    // async ipapi.co lookup hadn't populated the country cache before
    // /verify was submitted, causing US-based users to land on
    // residence_country='IN' and be credited ₹25 instead of $1.
    // TZ is always available on the browser (Intl API is synchronous)
    // and reliably distinguishes India from everywhere else.
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) config.headers['X-Detected-TZ'] = tz;
    } catch { /* Intl not available — skip silently */ }
    return config;
});

api.interceptors.response.use(
    (r) => r,
    (error) => {
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('ac_token');
            localStorage.removeItem('ac_user');
            const path = window.location.pathname;
            const isAdminPath = path.startsWith('/admin');
            const publicPaths = ['/', '/login', '/signup', '/forgot', '/admin/login'];
            const isPublic = publicPaths.some(p => path === p || path.startsWith(p + '/'));
            if (!isPublic) {
                window.location.href = isAdminPath ? '/admin/login' : '/login';
            }
        }
        // Round 38 — attach a guaranteed-string `userMessage` on every
        // rejected error so a caller that writes `toast.error(err)` or
        // `toast.error(err.userMessage)` is always safe, even if the
        // backend `detail` is a structured object (FastAPI 422 array,
        // EMAIL_NOT_VERIFIED dict, etc.). This + the lib/toast.js
        // coercion together make the React-#31 "object as child" bug
        // class unreachable from new code paths.
        try { error.userMessage = readErr(error); } catch { /* noop */ }

        // Round 39 — observability: surface every 4xx/5xx as a GTM
        // `app_error` event so production incidents show up live in
        // analytics. `gtmPush` is a no-op outside the production
        // hostname allow-list (see lib/gtm.js), so preview / staging /
        // local-dev errors never pollute the prod dashboards.
        try {
            const status = error?.response?.status;
            if (status && status >= 400) {
                const cfg = error?.config || {};
                let path = '';
                try {
                    path = String(cfg.url || '');
                    if (path.startsWith(API)) path = path.slice(API.length);
                } catch { /* noop */ }
                gtmPush({
                    event: 'app_error',
                    code: status,
                    path,
                    method: (cfg.method || 'GET').toUpperCase(),
                    message: error.userMessage,
                });
            }
        } catch { /* analytics must never break the auth/error flow */ }

        return Promise.reject(error);
    }
);

/**
 * Safely turn an axios error into a user-readable string.
 *
 * FastAPI returns 422 validation errors as a list of dicts
 * (`{type, loc, msg, input, url}`). Passing that array straight to a
 * React child / Sonner toast crashes with
 * "Objects are not valid as a React child". This helper normalises
 * any error into a plain string the UI can safely render.
 */
export function readErr(error, fallback = 'Something went wrong') {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
        // 422 — list of field errors. Show the first humanly-relevant message.
        const first = detail[0];
        if (first && typeof first === 'object') {
            const field = Array.isArray(first.loc) ? first.loc.filter(p => p !== 'body').join('.') : '';
            const msg = first.msg || first.message || 'Invalid input';
            return field ? `${field}: ${msg}` : msg;
        }
        if (typeof first === 'string') return first;
    }
    if (detail && typeof detail === 'object') {
        return detail.msg || detail.message || JSON.stringify(detail);
    }
    return error?.message || fallback;
}
